import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { getTaxYearList } from "../../api/services/taxYear";
import { getPersonLists, createPersonList } from "../../api/services/personList";
import { getPersonCategoryList } from "../../api/services/personListCategory";

import {
    Page,
    PageHeader,
    Select,
    Option,
    Row,
    Col,
    Button,
    Popup,
    PopupTitle,
    PopupContent,
    PopupFooter,
    Form,
    FormItem,
    Input,
    ItemList,
    Toast,
    Text,
    Badge,
    Card,
    CardTitle,
    CardContent,
    Image,
    FormButtons
} from "@tv2no/ui-react";

export const PersonLists = () => {
    const params = useParams();
    const navigate = useNavigate();
    const selectedYear = params.year;

    const [showNewListPopup, setShowNewListPopup] = useState(false);
    const [newListName, setNewListName] = useState(null);
    const [newListCategoryId, setNewListCategoryId] = useState(null);
    const [busyCreatingList, setBusyCreatingList] = useState(false);
    const [busyFetchingCategoryList, setBusyFetchingCategoryList] = useState(false);
    const [personListCategories, setPersonListCategories] = useState(null);
    const [yearList, setYearList] = useState(null);
    const [personLists, setPersonLists] = useState(null);

    /**
     * Fetch tax year list
     * @private
     */
    const fetchTaxYearList = () => getTaxYearList().then((res) => setYearList(res));

    /**
     * Fetch person list categories
     * @private
     */
    const fetchPersonListCategories = () => {
        setBusyFetchingCategoryList(true);

        getPersonCategoryList().then((res) => {
            setPersonListCategories(res);
            setBusyFetchingCategoryList(false);
        });
    };

    /**
     * Fetch person list
     * @private
     */
    const fetchPersonLists = useCallback(
        (year) => {
            // Tax year
            navigate(`/lists/${year}`, { replace: true });

            // Fetch year
            getPersonLists(year).then((res) => setPersonLists(res));
        },
        [navigate]
    );

    /**
     * Create new list
     */
    const handleCreateList = () => {
        if (!newListName || !newListCategoryId) {
            return null;
        }

        const activeYear = params.year;

        // Busy on
        setBusyCreatingList(true);

        // Send request
        createPersonList({
            year: activeYear,
            name: newListName,
            categoryId: newListCategoryId
        })
            .then((newList) => {
                navigate(`/lists/entry/${newList.id}`);
            })
            .catch(() => {
                Toast.error("Feil ved opprettelse av liste");
                setBusyCreatingList(false);
            });
    };

    useEffect(() => {
        fetchTaxYearList();
        fetchPersonListCategories();

        if (selectedYear) {
            fetchPersonLists(selectedYear);
        }
    }, [fetchPersonLists, selectedYear]);

    return (
        <Page>
            <PageHeader
                icon="star"
                title={`Topplister ${selectedYear || ""}`}
                subtitle="Skattelister"
            />
            <Row>
                <Col span={24}>
                    <Card>
                        <CardTitle title="Velg skatteår" />
                        <CardContent>
                            <Row gutter={["small", "small"]}>
                                <Col>
                                    <Select
                                        placeholder="Velg skatteår"
                                        value={selectedYear}
                                        onChange={fetchPersonLists}
                                    >
                                        {yearList &&
                                            yearList.map((entry) => (
                                                <Option key={entry.year} value={entry.year}>
                                                    {entry.year}
                                                </Option>
                                            ))}
                                    </Select>
                                </Col>
                                <Col>
                                    <Button
                                        block
                                        icon="plus"
                                        color="primary"
                                        disabled={!selectedYear}
                                        onClick={() => setShowNewListPopup(true)}
                                    >
                                        Lag ny liste
                                    </Button>
                                </Col>
                            </Row>
                        </CardContent>
                    </Card>
                </Col>
                {/* Lists */}
                {selectedYear &&
                    personListCategories &&
                    personListCategories.map((category) => (
                        <Col key={category.id} span={24}>
                            <Card collapsable>
                                <CardTitle title={category.name} />
                                <CardContent>
                                    <ItemList
                                        busy={busyFetchingCategoryList}
                                        onItemClick={(itemId) => navigate(`/lists/entry/${itemId}`)}
                                    >
                                        {personLists &&
                                            personLists
                                                .filter((l) => l.category.id === category.id)
                                                .map((entry, index) => (
                                                    <ItemList.Item
                                                        index={index}
                                                        key={entry.id}
                                                        value={entry.id}
                                                        prepend={
                                                            <Image
                                                                src={
                                                                    entry.thumbnail &&
                                                                    entry.thumbnail.url
                                                                }
                                                                thumbnail="regular"
                                                            />
                                                        }
                                                        append={
                                                            entry.published ? (
                                                                <Badge fill color="success">
                                                                    Publisert
                                                                </Badge>
                                                            ) : (
                                                                <Badge fill color="error">
                                                                    Upublisert
                                                                </Badge>
                                                            )
                                                        }
                                                    >
                                                        <Text block>{entry.name}</Text>
                                                        <Text size="small" opacity={40}>
                                                            {entry.description ||
                                                                "Ingen beskrivelse"}
                                                        </Text>
                                                    </ItemList.Item>
                                                ))}
                                    </ItemList>
                                </CardContent>
                            </Card>
                        </Col>
                    ))}
            </Row>

            {showNewListPopup && (
                <Popup>
                    <PopupTitle>Lag ny liste</PopupTitle>
                    <PopupContent>
                        <Form>
                            <Row>
                                <Col span={24}>
                                    <FormItem label="Navn">
                                        <Input value={newListName} onChange={setNewListName} />
                                    </FormItem>
                                </Col>
                                <Col span={24}>
                                    <FormItem label="Kategori">
                                        <Select
                                            placeholder="Velg en kategori"
                                            value={newListCategoryId}
                                            onChange={(val) => setNewListCategoryId(val)}
                                        >
                                            {personListCategories &&
                                                personListCategories.map((cat) => (
                                                    <Option key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </FormItem>
                                </Col>
                            </Row>
                        </Form>
                    </PopupContent>
                    <PopupFooter>
                        <FormButtons>
                            <Button
                                type="submit"
                                color="primary"
                                onClick={handleCreateList}
                                busy={busyCreatingList}
                            >
                                Opprett liste
                            </Button>
                            <Button
                                onClick={() => setShowNewListPopup(false)}
                                disabled={busyCreatingList}
                            >
                                Avbryt
                            </Button>
                        </FormButtons>
                    </PopupFooter>
                </Popup>
            )}
        </Page>
    );
};
